import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';

import { featureEnabled } from 'helpers/ToggleBot';

import DrawerBody from 'components/ui/DrawerBody';
import DrawerHeader from 'components/ui/DrawerHeader';
import DrawerFooter from 'components/ui/DrawerFooter';
import SuccessFace from 'components/ui/AnimatedStates/SuccessFace';
import ErrorFace from 'components/ui/AnimatedStates/ErrorFace';
import LoadingFace from 'components/ui/AnimatedStates/LoadingFace';
import WarningFace from 'components/ui/AnimatedStates/WarningFace';

import { downloadGenericPdf } from 'actions/landlordNoticesActions';

import { getTranslatedString } from 'helpers/i18n';
import NoticeForm from './NoticeForm/NoticeForm';

import { createNotice } from './store/actions';

const CREATING_STATES = {
  form: 'form',
  loading: 'loading',
  success: 'success',
  error: 'error',
};

const FORM_NAME = 'create_notice_form';

const CreateNotice = ({ handleClose, onSubmissionComplete, createNotice, downloadGenericPdf }) => {
  const [creatingState, setCreatingState] = useState(CREATING_STATES.form);
  const [tempValues, setTempValues] = useState({});
  const [createdNoticeId, setCreatedNoticeId] = useState();
  const [ableToDeliverNotice, setAbleToDeliverNotice] = useState([]);
  const [noticeDetails, setNoticeDetails] = useState({});

  const handleCreateNotice = useCallback(
    async (values) => {
      setCreatingState(CREATING_STATES.loading);
      try {
        // if the request fails, store the values temporarily to retry
        setTempValues(values);
        const response = await createNotice(values);

        setTempValues({});
        setCreatedNoticeId(response.notice_details.notice_id);
        if (onSubmissionComplete) {
          onSubmissionComplete();
        }
        setCreatingState(CREATING_STATES.success);
        if (
          featureEnabled('capreit_specific_notification_preferences') &&
          !response.notice_details.able_to_deliver_notice
        ) {
          setAbleToDeliverNotice(response.notice_details.able_to_deliver_notice);
          setNoticeDetails(response.notice_details.single_unit_notice_details);
        }
      } catch (err) {
        setCreatingState(CREATING_STATES.error);
      }
    },
    [createNotice, onSubmissionComplete]
  );

  const handleRetry = useCallback(() => {
    handleCreateNotice(tempValues);
  }, [handleCreateNotice, tempValues]);

  if (featureEnabled('capreit_specific_notification_preferences') && !ableToDeliverNotice) {
    return (
      <ElectronicDeliveryUnavailable
        downloadNotice={() => downloadGenericPdf(createdNoticeId)}
        handleClose={handleClose}
      />
    );
  }

  if (creatingState === CREATING_STATES.form) {
    return <FormFace handleClose={handleClose} handleCreateNotice={handleCreateNotice} />;
  } else if (creatingState === CREATING_STATES.loading) {
    return (
      <LoadingFace
        headerText={getTranslatedString('landlordPortal.notices.createLoading')}
        bodyText={getTranslatedString('globalDrawer.loadingBody')}
      />
    );
  } else if (creatingState === CREATING_STATES.success) {
    return (
      <SuccessFace
        onClick={handleClose}
        headerText={getTranslatedString('landlordPortal.notices.createSuccess')}
        bodyText={getTranslatedString('globalDrawer.successBody')}
        buttonText={getTranslatedString('close')}
      />
    );
  } else if (creatingState === CREATING_STATES.error) {
    return (
      <ErrorFace
        headerText={getTranslatedString('landlordPortal.notices.createError')}
        bodyText={getTranslatedString('globalDrawer.errorBody')}
        onClick={handleClose}
        buttonText={getTranslatedString('close')}
        onSecondaryClick={handleRetry}
        secondaryButtonText={getTranslatedString('globalDrawer.goBack')}
      />
    );
  }
};

const FormFace = ({ handleClose, handleCreateNotice }) => {
  return (
    <React.Fragment>
      <DrawerHeader
        onClose={handleClose}
        title={getTranslatedString('landlordPortal.addNoticeDialog.addNewNotice')}
        subHeader={getTranslatedString('landlordPortal.notices.requiredField')}
      />
      <DrawerBody>
        <NoticeForm formName={FORM_NAME} formData={{}} handleSubmit={handleCreateNotice} />
      </DrawerBody>
      <DrawerFooter
        primaryActionFormName={FORM_NAME}
        primaryActionText={getTranslatedString('create')}
      />
    </React.Fragment>
  );
};

const mapStoreToProps = (store) => ({});

const mapDispatchToProps = {
  createNotice,
  downloadGenericPdf,
};

export default connect(mapStoreToProps, mapDispatchToProps)(CreateNotice);

const ElectronicDeliveryUnavailable = ({ downloadNotice, handleClose }) => {
  return (
    <WarningFace
      headerText={getTranslatedString("landlordPortal.addNoticeTemplate.electronicDeliveryUnavailable")}
      bodyText={
        <div>
          <div>{getTranslatedString("landlordPortal.addNotice.oneOrMoreWillNotReceive")}</div>
        </div>
      }
      onClick={downloadNotice}
      buttonText={getTranslatedString("landlordPortal.addNoticeTemplate.downloadNotice")}
      onSecondaryClick={handleClose}
      secondaryButtonText={getTranslatedString('close')}
    />
  );
};
